import { Box, Stack } from '@mui/material';
import { useEffect } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { ApplicationConfigurationFormShape } from '@/pages/admin/ApplicationConfigurationForm/ApplicationConfigurationFormFields';
import { PresentationReviewField } from '@/pages/admin/ApplicationConfigurationForm/PresentationReviewField';
import { PresentationApprovalRequirement } from '@/types/schema';

import {
  useOrganizationConfigurationFormQuery,
  useUpdateOrganizationConfigurationFormMutation,
} from './graphql/OrganizationConfigurationForm.generated';

type OrganizationPresentationSettingsFormShape = Pick<
  ApplicationConfigurationFormShape,
  'allowUserPresentationTemplates' | 'requirePresentationApproval'
>;

interface OrganizationPresentationSettingsFormProps {
  luminaryOrganizationID: string;
  readOnly?: boolean;
}

export function OrganizationPresentationSettingsForm({
  luminaryOrganizationID,
  readOnly = false,
}: OrganizationPresentationSettingsFormProps) {
  const formMethods = useForm<OrganizationPresentationSettingsFormShape>({
    defaultValues: {
      allowUserPresentationTemplates: true,
      requirePresentationApproval: PresentationApprovalRequirement.NotRequired,
    },
  });
  const { control, handleSubmit, reset } = formMethods;
  const { createSuccessFeedback, showFeedback } = useFeedback();

  // OrganizationConfigurationForm runs the same query and shows the error
  // feedback if it fails, so this form does not show it again.
  const { data } = useOrganizationConfigurationFormQuery({
    variables: { luminaryOrganizationID },
  });
  const config = data?.organizationApplicationConfigurations.edges?.[0]?.node;

  useEffect(() => {
    if (!config) return;
    reset({
      allowUserPresentationTemplates: config.allowUserPresentationTemplates,
      requirePresentationApproval: config.requirePresentationApproval,
    });
  }, [config, reset]);

  const [updateOrgConfiguration, { loading }] =
    useUpdateOrganizationConfigurationFormMutation({
      onCompleted: createSuccessFeedback(
        'Successfully updated presentation settings'
      ),
      onError: (error) => {
        const userFacingMessages = getUserFacingErrorMessages(error);
        if (userFacingMessages.length > 0) {
          showFeedback(userFacingMessages.join('. '));
        } else {
          showFeedback(
            'There was an error updating the presentation settings. Please try again.'
          );
        }
      },
    });

  const onSubmit = handleSubmit((formValues) => {
    if (!config) {
      showFeedback(
        'No organization configuration found. Please ensure the organization has been fully provisioned.'
      );
      return;
    }

    // Send only the fields in this card, so the update does not change the
    // values in the application configuration card.
    return updateOrgConfiguration({
      variables: {
        input: {
          id: config.id,
          update: {
            allowUserPresentationTemplates:
              formValues.allowUserPresentationTemplates,
            requirePresentationApproval: formValues.requirePresentationApproval,
          },
        },
      },
    });
  });

  return (
    <FormProvider {...formMethods}>
      <FormFieldsDisabledProvider isDisabled={readOnly}>
        <Stack component="form" spacing={2} maxWidth={400} onSubmit={onSubmit}>
          <PresentationReviewField />
          <FormAwareCheckbox
            control={control}
            label="Allow user presentation templates"
            helpText="If unchecked, users cannot create, duplicate, update, or materialize presentation templates."
            fieldName="allowUserPresentationTemplates"
          />
          {!readOnly && (
            <Box>
              <Button
                loading={loading}
                variant="primary"
                type="submit"
                size="sm"
              >
                Save updates
              </Button>
            </Box>
          )}
        </Stack>
      </FormFieldsDisabledProvider>
    </FormProvider>
  );
}
